import { useMemo, useState } from 'react';
import { FilterCard, SectionCard } from '@/components/DataList';
import { ListTable, ScreenFrame } from '@/components/Screen';
import { Icon } from '@/components/icons';
import { admin } from '@/api/endpoints';
import { errorMessage, pickList, type JsonMap } from '@/api/client';
import { useAsync } from '@/hooks/useAsync';
import {
  Button,
  Empty,
  ErrorBox,
  Loading,
  Money,
  ProSearch,
  Select,
  StatusBadge,
} from '@/components/ui';
import { t } from '@/i18n';
import { str } from '@/utils/format';

function amountSuffix(currency: unknown, walletKey: unknown): string {
  const cur = str(currency, '').toUpperCase();
  if (cur === 'USD' || cur === '$') return '$';
  if (str(walletKey) === 'games') return '$';
  return 'SYP';
}

export function DepositsScreen() {
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');

  const state = useAsync(
    () => admin.deposits(),
    (p) => pickList(p, 'data', 'deposits', 'items'),
  );

  const list = state.data ?? [];

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return list.filter((d) => {
      const status = str(d.status, '').toLowerCase();
      if (statusFilter !== 'all' && status !== statusFilter) return false;
      if (!needle) return true;
      const hay = [
        str(d.user_name),
        str(d.user_phone ?? d.phone),
        str(d.method_name ?? d.payment_method ?? d.method),
        str(d.tx_reference),
      ]
        .join(' ')
        .toLowerCase();
      return hay.includes(needle);
    });
  }, [list, q, statusFilter]);

  async function review(id: unknown, action: 'approve' | 'reject') {
    const promptText = action === 'approve' ? 'ملاحظة الموافقة' : 'سبب الرفض';
    const adminNote = window.prompt(promptText, '');
    if (adminNote === null) return;
    const res = await admin.reviewDeposit({ id, action, admin_note: adminNote });
    if (!res.success) {
      window.alert(errorMessage(res));
      return;
    }
    state.reload();
  }

  if (state.loading) return <Loading />;
  if (state.error) return <ErrorBox message={state.error} onRetry={state.reload} />;

  return (
    <ScreenFrame
      mode="admin"
      icon="deposit"
      title={t('deposits.title')}
      subtitle={t('deposits.subtitle')}
    >
      <FilterCard title={t('common.search')} icon={<Icon name="search" size={24} />}>
        <ProSearch value={q} onChange={setQ} placeholder={t('common.search')} />
        <Select
          label={t('common.status')}
          value={statusFilter}
          onChange={setStatusFilter}
          options={[
            { value: 'pending', label: t('common.pending') },
            { value: 'approved', label: t('common.approved') },
            { value: 'rejected', label: t('common.rejected') },
            { value: 'all', label: t('common.all') },
          ]}
        />
        <Button type="button" variant="secondary" onClick={() => state.reload()}>
          <Icon name="refresh" size={18} /> {t('common.refresh')}
        </Button>
      </FilterCard>

      <SectionCard title={t('nav.deposits')} icon={<Icon name="orders" size={24} />}>
        {filtered.length === 0 ? (
          <Empty title={t('deposits.empty')} hint={t('deposits.empty')} />
        ) : (
          <ListTable tableClassName="deposits-table">
            <thead>
              <tr>
                <th>الزبون</th>
                <th>المحفظة</th>
                <th>الطريقة</th>
                <th>المبلغ</th>
                <th>المرجع</th>
                <th>الحالة</th>
                <th>إجراءات</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((d: JsonMap) => {
                const pending = str(d.status).toLowerCase() === 'pending';
                return (
                  <tr key={str(d.id)}>
                    <td>
                      <strong>{str(d.user_name)}</strong>
                      <small dir="ltr">{str(d.user_phone ?? d.phone, '—')}</small>
                    </td>
                    <td>{str(d.wallet_key) === 'games' ? 'الألعاب' : 'الوحدات'}</td>
                    <td>{str(d.method_name ?? d.method, '—')}</td>
                    <td>
                      <Money
                        value={d.expected_amount ?? d.amount}
                        suffix={amountSuffix(d.currency, d.wallet_key)}
                      />
                    </td>
                    <td dir="ltr">{str(d.tx_reference, '-')}</td>
                    <td>
                      <StatusBadge status={d.status} />
                    </td>
                    <td>
                      {pending ? (
                        <div className="table-actions">
                          <button
                            type="button"
                            className="icon-btn ok"
                            title="موافقة"
                            onClick={() => void review(d.id, 'approve')}
                          >
                            <Icon name="check" size={16} />
                          </button>
                          <button
                            type="button"
                            className="icon-btn danger"
                            title="رفض"
                            onClick={() => void review(d.id, 'reject')}
                          >
                            <Icon name="close" size={16} />
                          </button>
                        </div>
                      ) : (
                        <small>{str(d.admin_note, '-')}</small>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </ListTable>
        )}
      </SectionCard>
    </ScreenFrame>
  );
}
